import * as Sentry from '@sentry/nextjs';
import { redactAuthSecrets } from './lib/auth-secret-urls';

const dsn = process.env.NEXT_PUBLIC_SENTRY_DSN;

// Unactionable third-party / browser-runtime noise, filtered with tight regexes so genuine app
// errors still surface. Firebase Auth is this app's only IndexedDB user and polls it every 800ms
// from an uncatchable `setInterval(async () => this._poll())` (@firebase/auth); when a browser
// tears the IDB connection down (WebKit on backgrounded/bfcached pages) or the SDK's internal
// state races, the rejection escapes a promise setInterval discards — unreachable from app code,
// and it self-heals on the next tick. The cross-origin-frame entry is thrown by injected
// browser/extension scripts (e.g. UC Browser's "sniff") reaching into our Firebase auth iframe.
const IGNORED_ERRORS = [
  /Connection to Indexed Database server lost/i, // AKSADEMIA-DASHBOARD-5
  /looking up record in object store/i, // AKSADEMIA-DASHBOARD-7 (IDB read during teardown)
  /INTERNAL ASSERTION FAILED: Pending promise was never set/i, // AKSADEMIA-DASHBOARD-4 (@firebase internal)
  /Blocked a frame with origin .* from accessing a cross-origin frame/i, // AKSADEMIA-DASHBOARD-8
];

function redactEventUrl<T extends { request?: { url?: string } }>(event: T): T {
  const url = event.request?.url;
  if (!url) return event;
  return { ...event, request: { ...event.request, url: redactAuthSecrets(url) } };
}

// Client-side init. No-op when NEXT_PUBLIC_SENTRY_DSN is unset.
// No tracing here (no tracesSampleRate, no onRouterTransitionStart export): nothing read
// the browser trace data. Error capture is unaffected; server/edge tracing in
// instrumentation.ts is unchanged. This does NOT shrink the bundle: under Turbopack,
// @sentry/nextjs 10.67 ignores bundleSizeOptimizations, so the SDK ships as before.
if (dsn) {
  Sentry.init({
    dsn,
    environment: process.env.NODE_ENV,
    release: process.env.NEXT_PUBLIC_SENTRY_RELEASE,
    ignoreErrors: IGNORED_ERRORS,
    // The auth action pages are opened with a single-use code in the query string.
    beforeSend: redactEventUrl,
    beforeSendTransaction: redactEventUrl,
    beforeBreadcrumb: (breadcrumb) => {
      const data = breadcrumb.data;
      if (!data) return breadcrumb;
      const redacted = Object.fromEntries(
        Object.entries(data).map(([key, value]) => [key, typeof value === 'string' ? redactAuthSecrets(value) : value]),
      );
      return { ...breadcrumb, data: redacted };
    },
  });
}
